/**
 * 登录场景
 * 处理用户注册和登录（使用独立的 HTTP API，不依赖 WebSocket）
 */
import DebugHelper from './modules/DebugHelper.js';
import ServerConfig from './modules/ServerConfig.js';
import GameStateStore from './modules/GameStateStore.js';
import NetworkManager from './modules/NetworkManager.js';
import TitleBackground from './modules/Title/TitleBackground.js';
import LoginFormOverlay from './modules/Login/LoginFormOverlay.js';

export default class LoginScene extends Phaser.Scene {
    constructor() {
        super('LoginScene');
    }

    init() {
        this.apiBaseUrl = ServerConfig.getApiBaseUrl();
        this.userId = null;
        this.username = null;
        this.playerData = null;
        this.mode = 'login';
        this.background = null;
        this.formOverlay = null;
        this.networkManager = null;
    }

    create() {
        this.background = new TitleBackground(this);
        this.background.create();

        this.formOverlay = new LoginFormOverlay(this, {
            onSubmit: (mode, values) => this.handleFormSubmit(mode, values),
            onModeChange: (mode) => {
                this.mode = mode;
            }
        });
        this.formOverlay.create();

        this.events.once('shutdown', () => this.cleanup());
        this.events.once('destroy', () => this.cleanup());
    }

    update(time) {
        if (this.background) {
            this.background.updateStarBackground(time);
        }
    }

    cleanup() {
        if (this.formOverlay) {
            this.formOverlay.destroy();
            this.formOverlay = null;
        }
    }

    handleFormSubmit(mode, values) {
        if (mode === 'register') {
            this.handleRegister(values);
        } else {
            this.handleLogin(values);
        }
    }

    async callAPI(endpoint, data) {
        const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.error || '请求失败');
        }

        return result;
    }

    async handleLogin(values) {
        const { username, password } = values;

        if (!username || !password) {
            this.formOverlay.setStatus('请输入用户名和密码', 'error');
            return;
        }

        this.formOverlay.setLoading(true);
        this.formOverlay.setStatus('正在登录...', 'loading');

        try {
            const result = await this.callAPI('/api/login', { username, password });

            if (result.success && result.data) {
                this.userId = result.data.userId;
                this.username = result.data.username;
                this.playerData = result.data.playerData || {};
                this.formOverlay.setStatus('登录成功！', 'success');

                await this.ensureGameServerConnection(username, password);

                this.time.delayedCall(600, () => {
                    this.enterTitle();
                });
            } else {
                this.formOverlay.setStatus(result.error || '登录失败', 'error');
                this.formOverlay.setLoading(false);
            }
        } catch (error) {
            DebugHelper.error('登录失败:', error);
            this.formOverlay.setStatus(`登录失败: ${error.message || '未知错误'}`, 'error');
            this.formOverlay.setLoading(false);
        }
    }

    async handleRegister(values) {
        const { username, password, email } = values;

        if (!username || !password) {
            this.formOverlay.setStatus('请输入用户名和密码', 'error');
            return;
        }

        if (username.length < 3 || username.length > 20) {
            this.formOverlay.setStatus('用户名长度须在 3-20 个字符之间', 'error');
            return;
        }

        if (password.length < 6) {
            this.formOverlay.setStatus('密码长度至少 6 个字符', 'error');
            return;
        }

        this.formOverlay.setLoading(true);
        this.formOverlay.setStatus('正在注册...', 'loading');

        try {
            const result = await this.callAPI('/api/register', {
                username,
                password,
                email: email || null
            });

            if (result.success) {
                this.formOverlay.setStatus('注册成功！请登录', 'success');
                this.formOverlay.setLoading(false);
                this.formOverlay.setMode('login');
            } else {
                this.formOverlay.setStatus(result.error || '注册失败', 'error');
                this.formOverlay.setLoading(false);
            }
        } catch (error) {
            DebugHelper.error('注册失败:', error);
            this.formOverlay.setStatus(`注册失败: ${error.message || '未知错误'}`, 'error');
            this.formOverlay.setLoading(false);
        }
    }

    async ensureGameServerConnection(username, password) {
        if (!this.networkManager) {
            this.networkManager = new NetworkManager(this);
        }

        if (this.networkManager.isConnected() && this.networkManager.userId) {
            window.loginNetworkManager = this.networkManager;
            return true;
        }

        return new Promise((resolve) => {
            let settled = false;
            const finish = (success) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                if (success) {
                    window.loginNetworkManager = this.networkManager;
                }
                resolve(success);
            };

            const unsubscribers = [];
            unsubscribers.push(this.networkManager.once('loginSuccess', () => finish(true)));
            unsubscribers.push(this.networkManager.once('error', () => finish(false)));
            unsubscribers.push(this.networkManager.once('connect', () => {
                this.networkManager.send('login', { username, password });
            }));

            this.networkManager.connect(ServerConfig.getServerUrl());

            const timeoutId = setTimeout(() => {
                unsubscribers.forEach((fn) => fn());
                DebugHelper.warn('LoginScene: WebSocket 连接超时，将使用 HTTP 登录缓存数据');
                finish(false);
            }, 8000);
        });
    }

    enterTitle() {
        window.userInfo = {
            userId: this.userId,
            username: this.username,
            playerData: this.playerData
        };

        if (this.playerData) {
            GameStateStore.applySavePayload(this.playerData);
        }

        window.loginNetworkManager = this.networkManager;

        const hasCharacter = this.checkHasCharacter();

        if (hasCharacter) {
            this.cleanup();
            this.scene.start('TitleScene');
        } else {
            this.formOverlay?.setStatus('请先选择角色', 'loading');
            this.time.delayedCall(600, () => {
                this.cleanup();
                this.scene.start('CharacterSelectScene', {
                    fromLogin: true
                });
            });
        }
    }

    checkHasCharacter() {
        if (this.playerData) {
            const player = this.playerData.player || this.playerData;
            if (player.name && player.element && (player.hp > 0 || player.maxHp > 0)) {
                return true;
            }
        }

        const player = GameStateStore.getPlayer();
        if (player?.name && player?.element && (player.hp > 0 || player.maxHp > 0)) {
            return true;
        }

        return false;
    }
}
